/** @format */

import {ReactElement} from 'react';

import {Box} from '@mui/material';
import {addDays, format} from 'date-fns';
import {ru} from 'date-fns/locale';
import i18n from 'i18next';
import {Trans} from 'react-i18next';

import {buildFormatterObject, getValueWithCurrency} from 'helpers/numberHelpers';

import {HintIconTooltip} from 'pages/Home/Main/Project/components/HintIconTooltip';

import {
  AmountText,
  NotAvailableText,
  SubText,
  SubTextContainer,
  WidgetTitle,
  WidgetTooltip,
} from './styles';

import {Delta} from '../Delta';
import {LabelText} from '../Delta/styles';
import {TooltipContent} from './components/TooltipContent';
import {useExpenditure} from './hooks/useExpenditure';
import {useYesterdayExpenditure} from './hooks/useYesterdayExpenditure';
import {ExpenditureWidgetProps} from './interfaces';
import {WidgetContainer} from '../../styles';

const ExpenditureWidget = ({
  status = 'default',
  mode = 'month',
  tooltipContent,
  tokenStatus,
}: ExpenditureWidgetProps): ReactElement => {
  const monthData = useExpenditure();
  const yesterdayData = useYesterdayExpenditure();

  const isYesterdayMode = mode === 'yesterday';

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('ru-RU').format(num);
  };

  const {format: formatCurrency} = buildFormatterObject(monthData.result.currency, i18n.language);

  const planValue = mode === 'month' ? monthData.result.plan : yesterdayData.result.dailyPlan;

  const getTitle = () => {
    if (isYesterdayMode) {
      return <Trans>Расход за вчера</Trans>;
    }

    if (tokenStatus === 'NOT_READY') {
      return <Trans>Расход за период</Trans>;
    }

    const monthResult = monthData.result;

    if (monthResult.startDateFull && monthResult.type === 'mediaplan') {
      return (
        <>
          <Trans>Расход с</Trans>&nbsp;
          {format(monthResult.startDateFull, 'd MMMM', {locale: ru})}
        </>
      );
    }
    return (
      <>
        <Trans>Расход за</Trans>&nbsp;
        {monthResult.currentMonth}
      </>
    );
  };

  const renderContent = () => {
    const loading = isYesterdayMode ? yesterdayData.loading : monthData.loading;

    if (loading) {
      return (
        <NotAvailableText>
          <Trans>загрузка...</Trans>
        </NotAvailableText>
      );
    }

    if (tokenStatus === 'NOT_READY') {
      const tomorrow = addDays(new Date(), 1);

      return (
        <NotAvailableText>
          <Trans>Будет доступен завтра</Trans>,{'\n'}
          {format(tomorrow, 'd MMMM', {locale: ru})}
        </NotAvailableText>
      );
    }

    if ((mode === 'month' && monthData.error) || (mode === 'yesterday' && yesterdayData.error)) {
      return (
        <NotAvailableText>
          <Trans>Пока не доступен</Trans>
        </NotAvailableText>
      );
    }

    if (isYesterdayMode) {
      const result = yesterdayData.result;
      const {value, currency} = formatCurrency(result.yesterdaySpent);

      return (
        <>
          <AmountText>
            {value}
            <Box component='span'>{currency}</Box>
          </AmountText>
          {result.hasPlan && (
            <SubText>
              из&nbsp;{formatNumber(Math.round(result.dailyPlan))}&nbsp;&nbsp;
              {currency}
            </SubText>
          )}
        </>
      );
    }

    const result = monthData.result;
    const {value, currency} = formatCurrency(result.spent);

    return (
      <>
        <AmountText>
          {value}
          <Box component='span'>{currency}</Box>
        </AmountText>
        {result.type === 'mediaplan' && result.plan > 0 && (
          <SubText>
            <Trans>из</Trans>&nbsp;{formatNumber(result.plan)}&nbsp;&nbsp;
            {currency}
          </SubText>
        )}
        {result.avgPerDay !== undefined && result.type === 'month' && (
          <SubText>
            &nbsp;<Trans>в день</Trans>:&nbsp;
            <b>{getValueWithCurrency(Math.round(result.avgPerDay), currency)}</b>
          </SubText>
        )}
      </>
    );
  };

  const renderBottomInfo = () => {
    if (status !== 'default') {
      return null;
    }

    if (tokenStatus === 'NOT_READY') {
      const {value, currency} = formatCurrency(planValue);

      return (
        <LabelText>
          <Trans>План</Trans>:&nbsp;
          <b>{getValueWithCurrency(value, currency)}</b>
        </LabelText>
      );
    }

    if (isYesterdayMode) {
      const result = yesterdayData.result;

      if (result.hasPlan) {
        const isOverspent = result.isOverspent;
        const label = (
          <>
            <Trans>{isOverspent ? 'Перерасход' : 'Недорасход'}</Trans>:
          </>
        );
        const {value, currency} = formatCurrency(result.difference);
        const text = getValueWithCurrency(value, currency);

        return <Delta label={label} value={text} isPositive={!isOverspent} />;
      }

      if (result.dayBeforeYesterdaySpent !== undefined) {
        const isGrowth = result.isGrowth;
        const label = <Trans>{isGrowth ? 'Вырос на' : 'Снизился на'}</Trans>;
        const {value, currency} = formatCurrency(result.comparisonDifference);
        const text = getValueWithCurrency(value, currency);

        return <Delta label={label} value={text} isPositive={isGrowth} />;
      }

      return null;
    }

    const result = monthData.result;

    if (result.type === 'mediaplan' && result.plan > 0) {
      const difference = result.plan - result.spent;
      const isPositive = difference > 0;
      const label = (
        <>
          <Trans>{isPositive ? 'Остаток' : 'Перерасход'}</Trans>:
        </>
      );

      const {value, currency} = formatCurrency(Math.abs(difference));
      const text = getValueWithCurrency(value, currency);

      return <Delta label={label} value={text} isPositive={isPositive} />;
    }

    if (
      result.difference !== undefined &&
      result.type === 'month' &&
      result.prevMonthSpent !== undefined
    ) {
      const isGrowth = result.isGrowth;
      const label = <Trans>{isGrowth ? 'Вырос на' : 'Снизился на'}</Trans>;
      const {value, currency} = formatCurrency(Math.abs(result.difference));
      const text = getValueWithCurrency(value, currency);

      return <Delta label={label} value={text} isPositive={isGrowth} />;
    }

    return null;
  };

  return (
    <WidgetContainer>
      <WidgetTitle>{getTitle()}</WidgetTitle>
      <WidgetTooltip>
        <HintIconTooltip
          title={tooltipContent || <TooltipContent mode={mode} />}
          placement='top'
          testId='project-expenditure'
        />
      </WidgetTooltip>

      {renderContent()}
      <SubTextContainer data-testid='project-expenditure'>{renderBottomInfo()}</SubTextContainer>
    </WidgetContainer>
  );
};

export default ExpenditureWidget;
